//@ts-nocheck
import { GridApi } from "ag-grid-community";
import classNames from "classnames";
import { FormInput } from "../../components/Form";
import { useToggle } from "../../hooks";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { Link } from "react-router-dom";

export type PaginationState = {
    offset: number;
    limit: number;
}

export type ServerSidePagination = {
    totalRows: number;
    handlePaginationChanged: (e: PaginationState) => void;
}

type PaginationProps = {
    gridProps: GridApi;
    pageSizes: number[];
    serverSidePagination?: ServerSidePagination;
};

export interface PaginationForwardRef {
    resetPagination: (ignoreCallback?: boolean) => void;
    getPagination: () => PaginationState;
}

const Pagination = forwardRef<PaginationForwardRef | undefined, PaginationProps>(({ gridProps, pageSizes, serverSidePagination }: PaginationProps, ref) => {
    useImperativeHandle(ref, () => ({
        resetPagination(ignoreCallback: boolean) {
            setPageIndex(0);
            if (ignoreCallback !== true) {
                if (serverSidePagination) {
                    serverSidePagination.handlePaginationChanged({offset: 0, limit: pageSize});
                } else {
                    gridProps.paginationGoToPage(0);
                }
            }
        },

        getPagination(): PaginationState {
            return { offset: pageIndex * pageSize, limit: pageSize };
        }
      }));

    const popupRef = useRef(null);
    const [isValidated, setIsValidated] = useToggle();
    /**
     * pagination count , index
     */
    const [pageIndex, setPageIndex] = useState<number>(serverSidePagination ? 0 : gridProps.paginationProxy.currentPage); // Page index is 0-indexed
    const [pageSize, setPageSize] = useState<number>(serverSidePagination ? (pageSizes ? pageSizes[0] : 20) : gridProps.paginationProxy.pageSize);
    const [pageCount, setPageCount] = useState<number>(serverSidePagination ? Math.ceil(serverSidePagination.totalRows / pageSize) : gridProps.paginationProxy.totalPages);

    const [popupPageIndex, setPopupPageIndex] = useState<number>(1); // Popup page index is 1-indexed

    const [pageSelectOpen, setPageSelectOpen] = useState<boolean>(false);

    useEffect(() => {
        if (serverSidePagination) {
            const newPageCount = Math.ceil(serverSidePagination?.totalRows / pageSize);
            setPageCount(newPageCount);
            setPageIndex(0);
        }
    }, [serverSidePagination?.totalRows]);

    useEffect(() => {
        serverSidePagination?.handlePaginationChanged({offset: pageIndex * pageSize, limit: pageSize});
    }, []);

    useEffect(() => {
        if (!serverSidePagination) {
            const handlePaginationChange = (event: PaginationChangedEvent) => {
                // Handle the pagination change
                setPageCount(event.api.paginationGetTotalPages());
                setPageIndex(event.api.paginationGetCurrentPage());
            };
    
            // Add event listener
            gridProps.addEventListener('paginationChanged', handlePaginationChange);
    
            // Clean up the event listener on component unmount
            return () => {
                gridProps.removeEventListener('paginationChanged', handlePaginationChange);
            };
        }
    }, [gridProps, serverSidePagination]);

    /**
     * get filter pages
     */
    const filterPages = useCallback(
        (visiblePages: number[], totalPages: number) => {
            return visiblePages.filter((page: number) => page <= pageCount);
        },
        [pageCount]
    );

    /**
     * handle visible pages
     */
    const getVisiblePages = useCallback(
        (page: number, total: number) => {
            if (total < 7) {
                return filterPages([1, 2, 3, 4, 5, 6], total);
            } else {
                if (page % 5 >= 0 && page > 4 && page + 2 < total) {
                    return [1, page - 1, page, page + 1, total];
                } else if (page % 5 >= 0 && page > 4 && page + 2 >= total) {
                    return [1, total - 3, total - 2, total - 1, total];
                } else {
                    return [1, 2, 3, 4, 5, total];
                }
            }
        },
        [filterPages]
    );

    /**
     * handle page change
     * @param page - current page
     * @returns
     */
    const changePage = (page: number) => {
        const activePage = pageIndex + 1;
        if (page === activePage) {
            return;
        }
        const visiblePages = getVisiblePages(page, pageCount);
        setVisiblePages(filterPages(visiblePages, pageCount));
        const newPageIndex = page - 1;
        if (serverSidePagination) {
            setPageIndex(newPageIndex);
            serverSidePagination.handlePaginationChanged({offset: newPageIndex * pageSize, limit: pageSize});
        } else {
            gridProps.paginationGoToPage(newPageIndex);
        }
    };

    useEffect(() => {
        const visiblePages = getVisiblePages(0, pageCount);
        setVisiblePages(visiblePages);
    }, [pageCount, getVisiblePages]);

    const [visiblePages, setVisiblePages] = useState<number[]>(getVisiblePages(0, pageCount));
    const activePage: number = pageIndex + 1;

    const jumpToPage = () => {
        gridProps.clearFocusedCell();
        if (popupRef.current.checkValidity() === true) {
            changePage(popupPageIndex);
            setPageSelectOpen(false);
        }
        setIsValidated();
    }

    const showPopup = () => {
        gridProps.clearFocusedCell();
        setPageSelectOpen(true);
    }

    useEffect(() => {
        console.log(gridProps.paginationProxy);
        console.log("Displayed row count is: ", gridProps.getDisplayedRowCount());
    }, [gridProps]);

    // const RecordCounter = () => {
    //     const numRecords = serverSidePagination ? serverSidePagination.totalRows : gridProps.paginationProxy.masterRowCount;
    //     let start = (pageIndex * pageSize) + 1;
    //     let end = (pageIndex * pageSize) + pageSize;

    //     if (end > numRecords) {
    //         end = numRecords;
    //     }

    //     if (numRecords === 0) {
    //         start = 0;
    //         end = 0;
    //     }

    //     return numRecords > 0 ? (
    //         <Badge className="d-flex justify-content-end mb-1" style={{width: "fit-content", flexWrap: "wrap", alignContent: "center"}}>
    //             <div>
    //                 {start > 0 ? <><b>{start}</b> - </>: <></>}{end > 0 ? <><b>{end}</b> of </>: <></>}<b>{numRecords}</b> records
    //             </div>
    //         </Badge>
    //     ) : <></>;
    // }

    return (
        <>
            <Modal size="sm" centered show={pageSelectOpen} onHide={() => setPageSelectOpen(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Jump to page {`(1 - ${pageCount})`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form validated={isValidated} ref={popupRef} onSubmit={(e) => { e.preventDefault();}}>
                <Row className="mt-3 mb-3 pe-0">
                    <Row className="ms-0">
                        <Col xs={4}>
                            <p className="popup-form-text-field">Jump to: </p>
                        </Col>
                        <Col xs={8} className="ps-0">
                                <FormInput
                                    autoFocus
                                    min={1}
                                    max={pageCount}
                                    required
                                    type="number"
                                    name="page-select"
                                    containerClass={"mb-2"}
                                    onChange={(e) => setPopupPageIndex(Number(e.target.value))}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter' && event.target.value) {
                                            jumpToPage();
                                        }
                                    }}
                                />
                        </Col>
                    </Row>
                </Row>
                <Modal.Footer>
                    <Button variant="secondary"
                        onClick={() => setPageSelectOpen(false)}
                    >Cancel</Button>
                    <Button variant="primary" onClick={jumpToPage}>Jump</Button>
                </Modal.Footer>
                </Form>
            </Modal.Body>
        </Modal>
        <Row>
            <div className="table-pagination d-lg-flex align-items-center mt-1 pt-2" style={{height: "2rem"}}>
            {
                <>
                    {pageSizes.length > 0 && (
                        <>
                            {<div className="d-inline-block me-3 mb-1">
                                <label className="me-1">Display :</label>
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        const newPageSize = Number(e.target.value);
                                        setPageSize(newPageSize);
                                        if (!serverSidePagination) {
                                            gridProps.paginationSetPageSize(newPageSize);
                                        } else {
                                            const newPageCount = Math.ceil(serverSidePagination.totalRows / newPageSize);
                                            setPageCount(newPageCount);
                                            serverSidePagination.handlePaginationChanged({offset: 0, limit: newPageSize});
                                            setPageIndex(0);
                                        }
                                    }}
                                    className="form-select d-inline-block w-auto"
                                >
                                    {(pageSizes || []).map((pageSize, index) => {
                                        return (
                                            <option key={index.toString()} value={pageSize}>
                                                {pageSize}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            }
                            
                        </>
                    )}
                </>}
                {pageCount > 1 && <>
                    <ul className="pagination pagination-rounded d-inline-flex align-item-center justify-content-end ms-auto mb-1">
                        <li key="prevpage"
                            className={classNames("page-item", "paginate_button", "previous", {
                                disabled: activePage === 1,
                            })}
                            onClick={() => {
                                if (activePage === 1) return;
                                changePage(activePage - 1);
                            }}
                        >
                            <Link to="#" className="page-link"><i className="mdi mdi-chevron-left"></i></Link>
                        </li>
                        {(visiblePages || []).map((page, index, array) => {
                            return array[index - 1] + 1 < page ? (
                                <React.Fragment key={page.toString()}>
                                    <li
                                        className="page-item d-md-inline-block"
                                        onClick={showPopup}
                                    >
                                        <Link to="#" className="page-link">...</Link>
                                    </li>
                                    <li className={classNames("page-item", "d-none", "d-md-inline-block", {
                                            active: activePage === page,
                                        })}
                                        onClick={(e) => changePage(page)}
                                    >
                                        <Link to="#" className="page-link">{page}</Link>
                                    </li>
                                </React.Fragment>
                            ) : (
                                <li
                                    className={classNames("page-item", "d-none", "d-md-inline-block", {
                                        active: activePage === page,
                                    })}
                                    onClick={(e) => changePage(page)}
                                >
                                    <Link to="#" className="page-link">{page}</Link>
                                </li>
                            );
                        })}
                        <li key="nextpage"
                            className={classNames("page-item", "paginate_button", "next", {
                                disabled: activePage === pageCount,
                            })}
                            onClick={() => {
                                if (activePage === pageCount) return;
                                changePage(activePage + 1);
                            }}
                        >
                            <Link to="#" className="page-link"><i className="mdi mdi-chevron-right"></i></Link>
                        </li>
                    </ul>
                    </>
                    }
                </div>
            </Row>
        </>
    );
});

export { Pagination };
